import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { API_BASE_URL } from '../api/config.js';

/**
 * The operator console, wearing the deployment's brand.
 *
 * The console was the one surface this product never rebranded: a hardcoded violet ramp and
 * the literal "GenEx Pay" in five places, while the app and every email had resolved their
 * brand from `/config` for months. An operator running a white-label deployment logged into
 * somebody else's product to administer their own.
 *
 * `/config` is PUBLIC, which is what makes this simple: the brand resolves before anyone has
 * signed in, so the login screen is branded too. It carries nothing sensitive by
 * construction, so fetching it without a token discloses nothing that visiting the app would
 * not.
 *
 * There is never an unbranded state. The bundled default is the value until the fetch
 * answers, and it stays the value if the fetch never does, so a console served while its API
 * is down still renders as itself rather than as a skeleton.
 *
 * Colour is applied by overwriting the two CSS custom properties the stylesheet already
 * derives everything from, rather than by threading a colour through components. The whole
 * sheet is written against `--primary`; setting it at the root rebrands every button, focus
 * ring and gradient at once, including the ones nobody remembers exist.
 */

export interface ConsoleBrand {
  readonly platformName: string;
  readonly primaryColor: string;
  readonly logoUrl: string | null;
  /** `remote` once /config has answered; `fallback` while loading or when it is unreachable. */
  readonly source: 'remote' | 'fallback';
}

/** Matches the app's bundled fallback, so an unreachable API looks the same in both. */
const DEFAULT_BRAND: ConsoleBrand = {
  platformName: 'GenEx Pay',
  primaryColor: '#5B2CE6',
  logoUrl: null,
  source: 'fallback',
};

const BrandContext = createContext<ConsoleBrand>(DEFAULT_BRAND);

export function useBrand(): ConsoleBrand {
  return useContext(BrandContext);
}

/** A hex colour, and nothing else. This value is written into a stylesheet. */
function isHexColour(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value);
}

/**
 * Darken a hex colour toward black by `amount` (0..1), for the hover shade.
 *
 * Derived rather than fetched because `/config` carries one primary, and asking an operator
 * to also nominate a hover colour is asking them to solve a problem the stylesheet can. A
 * fixed multiply keeps the relationship the hand-tuned violet ramp already had between
 * `--primary` and `--primary-hover`.
 */
function darken(hex: string, amount: number): string {
  const channel = (start: number): string => {
    const value = Number.parseInt(hex.slice(start, start + 2), 16);
    return Math.round(value * (1 - amount))
      .toString(16)
      .padStart(2, '0');
  };
  return `#${channel(1)}${channel(3)}${channel(5)}`;
}

export function BrandProvider({ children }: { readonly children: ReactNode }) {
  const [brand, setBrand] = useState<ConsoleBrand>(DEFAULT_BRAND);

  useEffect(() => {
    // With no configured API origin the console is running on its mock transport, and there
    // is no `/config` to ask. The bundled default is the right answer, not a failed request.
    if (API_BASE_URL === '') return;

    const controller = new AbortController();
    void (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/v1/config`, { signal: controller.signal });
        if (!response.ok) return;
        const body: unknown = await response.json();
        if (typeof body !== 'object' || body === null) return;

        const config = body as Record<string, unknown>;
        const platformName = config['platformName'];
        const primaryColor = config['primaryColor'];
        const logoUrl = config['logoUrl'];

        setBrand({
          platformName:
            typeof platformName === 'string' && platformName.length > 0
              ? platformName
              : DEFAULT_BRAND.platformName,
          // Validated, not trusted: this string is written into a stylesheet, and anything
          // that is not six hex digits has no business being there.
          primaryColor: isHexColour(primaryColor) ? primaryColor : DEFAULT_BRAND.primaryColor,
          // `/config` returns a PATH, so it is joined to the API origin here. The app hit
          // exactly this and rendered no logo at all for months.
          logoUrl: typeof logoUrl === 'string' && logoUrl.length > 0 ? `${API_BASE_URL}${logoUrl}` : null,
          source: 'remote',
        });
      } catch {
        // Offline, blocked by CORS, or the API is down. The default stands; a console that
        // cannot reach its API has bigger problems than its colour, and it must still render.
      }
    })();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--primary', brand.primaryColor);
    root.style.setProperty('--primary-hover', darken(brand.primaryColor, 0.18));
    // The sidebar and login gradients read the ramp directly rather than `--primary`, so the
    // two ends are pinned as well. Without this the chrome stays violet while the buttons
    // change, which looks more broken than leaving it all violet would have.
    root.style.setProperty('--violet-400', brand.primaryColor);
    root.style.setProperty('--violet-500', brand.primaryColor);
    root.style.setProperty('--violet-600', darken(brand.primaryColor, 0.18));
    root.style.setProperty('--violet-800', darken(brand.primaryColor, 0.5));
  }, [brand.primaryColor]);

  useEffect(() => {
    document.title = `${brand.platformName} Operator Console`;
  }, [brand.platformName]);

  const value = useMemo(() => brand, [brand]);
  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}
